import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useOrderDetail } from '../../../api/hooks/sales/useOrderDetail';
import { useOrderSearch } from '../../../api/hooks/sales/useOrderSearch';
import { useDebounce } from '../../../hooks/useDebounce';
import { useMobile } from '../../../hooks/useMobile';

import { ReactComponent as SearchIcon } from '../../../icons/search.svg';

import '../admin-panel/admin-panel.scss';
import './order-search-page.scss';

import { navigateToAdminNavItem } from '../../../route/adminNav';
import { ROUTE_PATH } from '../../../route/utils';
import { Currency } from '../../../utils/currency/Currency';
import { NAV_SECTIONS } from '../admin-panel/AdminPanel';
import { AdminNav } from '../../common/admin-nav/AdminNav';
import { StatusPill } from '../../common/status-pill/StatusPill';
import Navigation from '../../navigation/Navigation';

const OrderResultRow = ({ order, active, onClick }) => (
  <button
    type="button"
    className={`osp-result${active ? ' osp-result--active' : ''}`}
    onClick={onClick}
  >
    <div className="osp-result__head">
      <span className="osp-result__number">{order.orderNumber}</span>
      <Currency className="osp-result__amount" value={order.amount ?? 0} />
    </div>
    <div className="osp-result__sub">
      <span>{order.name || order.email || 'Unknown customer'}</span>
      <StatusPill type={order.paid ? 'active' : 'pending'}>
        {order.paid ? 'Paid' : 'Unpaid'}
      </StatusPill>
    </div>
  </button>
);

const OrderDetailPanel = ({ orderNumber }) => {
  const { data: order, loading } = useOrderDetail(orderNumber);

  if (!orderNumber) {
    return <div className="osp-empty">Search for an order to see its details.</div>;
  }

  if (loading) {
    return <div className="osp-empty">Loading order…</div>;
  }

  if (!order) {
    return <div className="osp-empty">Order not found.</div>;
  }

  return (
    <div className="osp-detail">
      <div className="osp-detail__header">
        <div>
          <span className="osp-detail__order-number">{order.orderNumber}</span>
          <span className="osp-detail__date">{order.orderDate}</span>
        </div>
        <StatusPill type={order.paid ? 'active' : 'pending'}>
          {order.paid ? 'Paid' : 'Unpaid'}
        </StatusPill>
      </div>

      <div className="osp-detail__section">
        <div className="osp-detail__section-title">Customer</div>
        <div className="osp-detail__customer-grid">
          <span className="osp-detail__label">Name</span>
          <span>{order.name || '-'}</span>
          <span className="osp-detail__label">Email</span>
          <span>{order.email || '-'}</span>
          <span className="osp-detail__label">Contact number</span>
          <span>{order.contactNumber || order.guestPhone || '-'}</span>
          <span className="osp-detail__label">Delivery address</span>
          <span>{order.address || '-'}</span>
        </div>
      </div>

      <div className="osp-detail__section">
        <div className="osp-detail__section-title">Items</div>
        <div className="osp-detail__items">
          {(order.items ?? []).map((item, index) => (
            <div className="osp-detail__item" key={index}>
              <div className="osp-detail__item-main">
                <span className="osp-detail__item-qty">{item.quantity}×</span>
                <span className="osp-detail__item-name">{item.name}</span>
                <Currency className="osp-detail__item-price" value={item.price ?? 0} />
              </div>
              {item.description && (
                <div className="osp-detail__item-description">{item.description}</div>
              )}
              {item.selectedOptions?.length > 0 && (
                <div className="osp-detail__item-options">{item.selectedOptions.join(' | ')}</div>
              )}
              {item.specialInstructions && (
                <div className="osp-detail__item-notes">Note: {item.specialInstructions}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="osp-detail__section">
        <div className="osp-detail__section-title">Totals</div>
        <div className="osp-detail__totals-grid">
          <span className="osp-detail__label">Subtotal</span>
          <Currency value={order.subtotal ?? 0} />
          <span className="osp-detail__label">Service fee</span>
          <Currency value={order.serviceFees ?? 0} />
          <span className="osp-detail__label">Delivery</span>
          <Currency value={order.delivery ?? 0} />
          {order.discount != null && (
            <>
              <span className="osp-detail__label">Discount</span>
              <Currency value={-order.discount} />
            </>
          )}
          <span className="osp-detail__label osp-detail__label--total">Total</span>
          <Currency className="osp-detail__total-amount" value={order.amount ?? 0} />
        </div>
      </div>
    </div>
  );
};

export const OrderSearchPage = () => {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const { data: results, loading } = useOrderSearch(debouncedQuery);
  const isMobile = useMobile();

  // On mobile there's no room for the nav sidebar + search/list alongside the detail view, and
  // there's no in-app entry point into the admin section on mobile anyway (see Navigation.jsx's
  // `!isMobile && isAdmin` gate on the Admin Panel link) — but a direct/shared link to a specific
  // order should still work, just as a focused detail-only view.
  if (isMobile && orderNumber) {
    return (
      <Navigation>
        <div className="order-search-page order-search-page--mobile-detail">
          <div className="osp-right osp-right--mobile">
            <OrderDetailPanel orderNumber={orderNumber} />
          </div>
        </div>
      </Navigation>
    );
  }

  return (
    <Navigation>
      <div className="admin-panel">
        <AdminNav
          sections={NAV_SECTIONS}
          activeSection="sales-orders"
          onChange={(key) => {
            const item = NAV_SECTIONS.flatMap((section) => section.items).find((i) => i.key === key);
            if (item) navigateToAdminNavItem(navigate, item);
          }}
        />
        <div className="admin-panel__content">
          <div className="admin-panel__title">Orders</div>
          <div className="admin-panel__panel">
            <div className="order-search-page">
              <div className="osp-left">
                <div className="osp-search">
                  <SearchIcon className="osp-search__icon" width={16} height={16} />
                  <input
                    className="osp-search__input"
                    placeholder="Search by order number…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
                <div className="osp-result-list">
                  {loading && <div className="osp-empty">Searching…</div>}
                  {!loading && results.length === 0 && (
                    <div className="osp-empty">No orders found.</div>
                  )}
                  {results.map((order) => (
                    <OrderResultRow
                      key={order.orderNumber}
                      order={order}
                      active={order.orderNumber === orderNumber}
                      onClick={() => navigate(`${ROUTE_PATH.ORDER}/${order.orderNumber}`)}
                    />
                  ))}
                </div>
              </div>
              <div className="osp-right">
                <OrderDetailPanel orderNumber={orderNumber} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Navigation>
  );
};
